import {
  CategoryType,
  type DocumentCategoryType,
  type DocumentRequirement,
  type DocumentVerification,
  type DocumentVerificationEvent,
  LogLevel,
  Status,
  VerificationEvent,
  type VerificationEventType
} from '~/libs/document-intelligence-types'
import {useDocIntelligenceApi} from '~/composables/document-intelligence/useDocIntelligenceApi'
import {ref, computed, readonly, onBeforeUnmount, type Ref} from 'vue'
import {storeToRefs} from 'pinia'
import {useAppAnalyticsStore} from '~/store/app-analytics'
import {useDocumentIntelligenceStore} from '~/store/document-intelligence'
import {docIntelligenceLog} from '~/libs/document-intelligence-helper'
import {useApplicationConfigStore} from '~/store/application-config'

export function useDocumentVerifications() {
  const appAnalyticsStore = useAppAnalyticsStore()
  const documentIntelligenceStore = useDocumentIntelligenceStore()
  const { documentVerifications, documentVerificationsEnabled } = storeToRefs(documentIntelligenceStore)
  const { fetchDocumentIntelligenceConfig } = useDocIntelligenceApi()
  const appConfigStore = useApplicationConfigStore()
  const { isDriversLicenseVerificationUiTestEnabled } = storeToRefs(appConfigStore)

  /*
   * ( LIFECYCLE )
   * Note: onBeforeUnmount only fires when used in component context
   *       this hook doesn't fire in middleware, any cleanup needed there should be handled separately
  */
  onBeforeUnmount(() => {
    _cleanUp()
  })

  /*
   * ( STATE )
  */
  const pendingDocumentVerifications: Ref<DocumentVerification[]> = ref([])

  /*
   * ( COMPUTED )
  */
  const fileInputProcessingStatus = computed(()  => {
    return function(documentId: number, documentCategory: DocumentCategoryType) {

      if (!documentId) {
        return null
      }

      const verification = _getDocumentVerification(documentId, documentCategory) || _getPendingVerification(documentId, documentCategory)
      if (!verification) {
        return null
      }

      return {
        processing: verification.status === Status.PROCESSING,
        error: verification?.passed === false && !!verification?.failureDetails?.message,
        message: verification?.failureDetails?.message,
      }
    }
  })

  /*
   * ( FUNCTIONS )
  */
  function setDocumentVerifications(enabled: boolean = false, verifications: DocumentVerification[] = []) {
    setDocumentVerificationsEnabled(enabled)
    if(!enabled) {
      return
    }

    documentIntelligenceStore.setDocumentVerifications(verifications)
  }

  function setDocumentVerificationsEnabled(enabled = false) {
    documentIntelligenceStore.setDocumentVerificationsEnabled(enabled)
  }

  const handleDocumentVerificationOnUpload = async (borrowerId: number, documentId: number, documentCategory: DocumentCategoryType) => {
    const canUpdateVerification = await _canUpdateVerification(documentCategory, borrowerId)
    if (!canUpdateVerification) {
      return
    }

    _addPendingVerification(documentId, documentCategory)

    logVerificationEvent(VerificationEvent.VERIFICATION_UPLOAD, [documentCategory])
  }

  function removeVerification(documentId: number) {
    pendingDocumentVerifications.value = pendingDocumentVerifications.value.filter(v => v.documentId !== documentId)
    documentIntelligenceStore.removeVerification(documentId)
  }

  async function handleDocumentVerificationEvent(type: string, documentVerificationEvent: DocumentVerificationEvent, borrowerId: number){
    const canUpdateVerification = await _canUpdateVerification(documentVerificationEvent.categoryType, borrowerId)
    if (type !== 'update' || !canUpdateVerification) {
      return;
    }

    const documentVerification: DocumentVerification = {
      documentId: documentVerificationEvent.documentId,
      categoryType: documentVerificationEvent.categoryType,
      status: documentVerificationEvent.status,
      passed: documentVerificationEvent.passed,
      failureDetails: documentVerificationEvent.passed === false ? {
        verificationType: documentVerificationEvent.verificationType,
        message: documentVerificationEvent.message,
        evaluatedType: documentVerificationEvent.evaluatedType
      } : null
    }

    documentIntelligenceStore.upsertDocumentVerification(documentVerification)

    if (documentVerification.passed === false) {
      logVerificationEvent(VerificationEvent.VERIFICATION_WARNING, [documentVerification.categoryType])
    }
  }

  function failedDocumentVerificationModalMessages(documentRequirements: DocumentRequirement[] = []) {
    return documentRequirements.map(req => {
      const document = req?.document
      if (!document?.id || !document?.category || !document?.filename) {
        return null
      }

      if (!_isCategoryTypeSupported(document.category)) {
        return null
      }

      const verificationState = _getDocumentVerification(document.id, document.category) || _getPendingVerification(document.id, document.category)

      if (verificationState?.passed === false && verificationState?.failureDetails?.message) {
        return {
          id: document.id,
          label: req?.name ?? req?.displayName ?? '',
          filename: document.filename,
          categoryType: verificationState?.categoryType
        }
      }
      return null
    }).filter(Boolean)
  }

  function logVerificationEvent(name: VerificationEventType, documentCategories: DocumentCategoryType[]) {
    appAnalyticsStore.fsTrackEvent({name, properties: { documentCategories }})
  }

  /*
   * ( PRIVATE FUNCTIONS )
  */
  function _cleanUp(){
    pendingDocumentVerifications.value = []
  }

  async function _verificationsEnabled(borrowerId: number): Promise<boolean> {
    if (documentVerificationsEnabled.value !== null) {
      return documentVerificationsEnabled.value
    }

    // prevents multiple requests
    if (documentIntelligenceStore.configPromise) {
      return documentIntelligenceStore.configPromise
    }

    const promise = fetchDocumentIntelligenceConfig(borrowerId)
      .then(response => {
        documentIntelligenceStore.setDocumentVerificationsEnabled(!!response?.data?.verificationsEnabled)
        return documentVerificationsEnabled.value as boolean
      })
      .catch(error => {
        docIntelligenceLog('Failed to fetch verification config', LogLevel.ERROR, { borrowerId, error })
        return false
      })
      .finally(() => {
        documentIntelligenceStore.setConfigPromise(null)
      })

    documentIntelligenceStore.setConfigPromise(promise)
    return promise
  }

  async function _canUpdateVerification(categoryType: DocumentCategoryType, borrowerId: number): Promise<boolean> {
    const verificationsEnabled = await _verificationsEnabled(borrowerId)
    if (!verificationsEnabled) {
      return false
    }

    return _isCategoryTypeSupported(categoryType)
  }

  const ALLOWED_CATEGORIES = [
    CategoryType.BUSINESS_BANK_STATEMENT,
    CategoryType.PERSONAL_BANK_STATEMENT,
    CategoryType.BUSINESS_TAX_RETURN,
    CategoryType.BUSINESS_TAX_RETURN_EXTENSION,
    CategoryType.PERSONAL_TAX_RETURN,
    CategoryType.PERSONAL_TAX_RETURN_EXTENSION,
    CategoryType.BALANCE_SHEET,
    CategoryType.PROFIT_AND_LOSS_CURRENT,
    CategoryType.PROFIT_AND_LOSS_PREVIOUS,
  ]

  function _isCategoryTypeSupported(categoryType: DocumentCategoryType): boolean {
    if (categoryType === CategoryType.DRIVERS_LICENSE_FRONT || categoryType === CategoryType.DRIVERS_LICENSE_BACK) {
      return isDriversLicenseVerificationUiTestEnabled.value === true
    }

    return ALLOWED_CATEGORIES.includes(categoryType)
  }

  function _getDocumentVerification(documentId: number, documentCategory: DocumentCategoryType): DocumentVerification | null {
    return documentVerifications.value.find(v => v.categoryType === documentCategory && v.documentId === documentId) || null
  }

  function _getPendingVerification(documentId: number, documentCategory: DocumentCategoryType): DocumentVerification | null {
    return pendingDocumentVerifications.value.find(v => v.documentId === documentId && v.categoryType === documentCategory) || null
  }

  function _addPendingVerification(documentId: number | null, documentCategory: DocumentCategoryType) {
    if (!documentId) {
      return
    }

    if (_getPendingVerification(documentId, documentCategory)) {
      return
    }

    const newPendingVerification: DocumentVerification = {
      documentId,
      categoryType: documentCategory,
      status: Status.PROCESSING,
      passed: null,
    }

    pendingDocumentVerifications.value.push(newPendingVerification)
  }

  return {
    // state
    pendingDocumentVerifications: readonly(pendingDocumentVerifications),
    // computed
    fileInputProcessingStatus,
    // actions
    handleDocumentVerificationOnUpload,
    removeVerification,
    handleDocumentVerificationEvent,
    failedDocumentVerificationModalMessages,
    setDocumentVerifications,
    logVerificationEvent,
  }
}
